import { getApiBaseUrl } from '../utils/backendUrl';
import { getAuthHeaders } from '../utils/authHeaders';

const BASE_URL = getApiBaseUrl();
const GET_CACHE_TTL_MS = 5000;
const inFlightRequests = new Map();
const responseCache = new Map();

function getFetchImpl() {
  if (typeof window !== 'undefined' && typeof window.fetch === 'function') {
    return window.fetch.bind(window);
  }
  if (typeof globalThis.fetch === 'function') {
    return globalThis.fetch.bind(globalThis);
  }
  return fetch;
}

/**
 * Shared apiClient function to call Backend APIs.
 * Automatically handles JSON and FormData (for file uploads)
 * and includes credentials for HttpOnly cookie authentication.
 */
export async function apiClient(endpoint, { body, ...customConfig } = {}) {
  const headers = {};

  // Do not set Content-Type header if body is FormData
  const isFormData = body instanceof FormData;
  if (body && !isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const authHeaders = getAuthHeaders();
  const method = (customConfig.method || (body ? 'POST' : 'GET')).toUpperCase();
  const config = {
    method,
    credentials: 'include', // Ensure HttpOnly cookies are automatically sent & received
    ...customConfig,
    headers: {
      ...authHeaders,
      ...headers,
      ...customConfig.headers,
    },
  };

  if (body) {
    config.body = isFormData ? body : (typeof body === 'string' ? body : JSON.stringify(body));
  }

  const url = endpoint.startsWith('http') ? endpoint : (
    endpoint.startsWith('/api') ? `${BASE_URL.replace(/\/api$/, '')}${endpoint}` : `${BASE_URL}${endpoint}`
  );

  const requestKey = `${method}:${url}`;
  const shouldCache = method === 'GET' && customConfig.cache !== false && !endpoint.includes('/auth/login') && !endpoint.includes('/auth/refresh');

  if (shouldCache) {
    const cachedEntry = responseCache.get(requestKey);
    if (cachedEntry && Date.now() - cachedEntry.timestamp < GET_CACHE_TTL_MS) {
      return cachedEntry.data;
    }

    const pendingRequest = inFlightRequests.get(requestKey);
    if (pendingRequest) {
      return pendingRequest;
    }
  }

  const requestPromise = (async () => {
    try {
      const fetchImpl = getFetchImpl();
      let response = await fetchImpl(url, config);

      // Handle 401 Unauthorized by attempting a silent token refresh via HttpOnly refresh token cookie
      if (response.status === 401 && !endpoint.includes('/auth/login') && !endpoint.includes('/auth/refresh') && !endpoint.includes('/auth/me')) {
        try {
          const refreshRes = await fetchImpl(`${BASE_URL}/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' }
          });

          if (refreshRes.ok) {
            // Retry original request with refreshed cookie session
            response = await fetchImpl(url, config);
          } else {
            // Refresh failed or revoked -> trigger session expiration
            if (window.location.pathname !== '/login' && window.location.pathname !== '/reset-password') {
              window.location.href = '/login';
            }
          }
        } catch (e) {
          console.error('Failed to auto-refresh session cookie:', e);
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `API error: ${response.status}`);
      }
      const data = await response.json();
      const formattedData = formatResponseData(data);

      if (shouldCache) {
        responseCache.set(requestKey, { data: formattedData, timestamp: Date.now() });
      }

      return formattedData;
    } catch (error) {
      console.error('API Client Error:', error);
      throw error;
    } finally {
      if (shouldCache && inFlightRequests.get(requestKey) === requestPromise) {
        inFlightRequests.delete(requestKey);
      }
    }
  })();

  if (shouldCache) {
    inFlightRequests.set(requestKey, requestPromise);
  }

  return requestPromise;
}

// Helper to capitalize first letters of each word in a string (Title Case)
function capitalizeWords(str) {
  if (typeof str !== 'string') return str;
  return str
    .split(' ')
    .map(word => {
      if (!word) return '';
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

// Helper to format artist names: capitalize each name and join with " / " instead of commas
function formatArtistNames(artistStr) {
  if (typeof artistStr !== 'string' || !artistStr.trim()) return artistStr;
  return artistStr
    .split(',')
    .map(name => capitalizeWords(name.trim()))
    .filter(name => name.length > 0)
    .join(' / ');
}

function formatResponseData(data) {
  if (!data) return data;
  if (Array.isArray(data)) {
    return data.map(item => formatResponseData(item));
  }
  if (typeof data === 'object') {
    const formattedObj = {};
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        let value = data[key];
        if (key === 'artist_name' || key === 'artistName' || key === 'singer_name' || key === 'singerName' || key === 'name') {
          value = capitalizeWords(value);
        } else if (key === 'artist' || key === 'artistSub' || key === 'lyrics' || key === 'music') {
          value = formatArtistNames(value);
        } else if (typeof value === 'object' && value !== null) {
          value = formatResponseData(value);
        }
        formattedObj[key] = value;
      }
    }
    return formattedObj;
  }
  return data;
}
